import { Directive, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { User } from '@api/models/user/user';
import { SmartyCubeStorageKeys } from '@core/config/smartycube-storage-keys';
import { CurrencyList, SearchCurrencyList } from '@core/constant/constants';
import { TranslationKey } from '@core/enums/translation-key.enum';
import { DateFormat, LoginUser, PaginationResult, StorageKeys } from '@smartera/core';
import { TableColumns } from '@smartera/core';
import { BaseApiService, LegacyBaseApiService, OldLegacyBaseApiService } from '@smartera/core';
import { UtilityService } from '@core/services/utility.service';
import { HeaderText, pdf } from '@smartera/core';
import { MenuItem, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { LabelValue } from '@smartera/core';
import { EncoloredField, GeneralSettingsObject, Key } from '@feature/smart-reports-core/models/type';

declare const zipcelx: any;

@Directive()
export class BaseComponent implements OnDestroy {
	public form: FormGroup;
	public searchForm: FormGroup;
	public searchToggle = false;

	//TODO: move report-util
	generalSettings: GeneralSettingsObject;
	public columnsList: any[] = [];
	public rowsList: any[] = [];

	// fields for conditionally formatting
	public conditionalFormattingField;
	public conditionalFormattingIndex;
	public conditionalFormattingObject;
	public conditionalFormattingConditions;

	// fields for encolouring fields
	encoloredFields: EncoloredField[] = [];
	protected colorFieldKey: string;

	public dateFormat = DateFormat.FORMAT_1;
	public onlyDateFormat = DateFormat.FORMAT_9;
	public calendar: any = {};

	public selectedColumns: TableColumns<any>;
	public columns: TableColumns<any> = [];
	public id = '';

	public dialogType = true; // true => create , false = update
	public dialogTitle = '';
	public isDialogVisible = false;

	public data;
	public total = 0;
	public sortKey = 'creDate,DESC';
	public pageNumber = 0;
	public sortField: string;
	public sortFieldDef: string;
	public sortOrder: 'DESC' | 'ASC';
	public pageSize = 10;
	public paginationTitle = this.t('tablePaginatorDescription');
	public selectedItem = null;

	// state
	protected statesKeys: string[] = [];
	public statusOptions: SelectItem[] = [];

	public activeIndex = 0; // step index
	public actionMenuModel: MenuItem[] = []; // action menu
	public stepItems: MenuItem[] = [];

	// search form filter
	public searchField: LabelValue<string, string>[] = [];
	public selectedSearchField: any[];

	/* angular service - manual injection by injector helper */

	public searchObject: any = {};

	// a variable for ui template
	public states: any = {};

	public minDate = new Date();

	public invalidForm = false;
	// file uplad varibles
	public file: any; // file
	public toDelete: any; // file to delete

	public utilityModel: MenuItem[] = [];

	private _user: LoginUser;
	public get user(): LoginUser {
		return this._user;
	}

	public set user(value) {
		this._user = value;
	}

	@ViewChild('pTable', { static: true }) public dt: Table;
	protected hideLoaderAfterInit = true;

	public rowsPerPage = [10, 25, 50, 100, 500];

	public currencyList = CurrencyList;

	public SearchCurrencyList = SearchCurrencyList;

	public builder: FormBuilder;

	// pdf things
	public pdfHeader: HeaderText = [];
	public removeLastColumn = true;

	private isLocalFilter = false;
	private localFilterForm: FormGroup;
	public cashData: any[] = [];
	protected searchDateFields = [];

	protected destroy$ = new Subject();

	@ViewChild('formsCon', { static: false })
	protected formsCon: ElementRef;

	public initStarted = true;

	constructor(
		protected componentService: LegacyBaseApiService<any> | OldLegacyBaseApiService<any> | BaseApiService<any>,
		protected utilServ: UtilityService
	) {
		this.builder = this.utilServ.builder;

		try {
			this.user = this.utilServ.loginUserService.user;
		} catch (error) {
			// console.log('error => ', this.loginUserService);
		}

		this.calendar = {
			firstDayOfWeek: 0,
			dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
			dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
			dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
			monthNames: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			],
			monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
			today: 'Today',
			clear: 'Clear',
		};

		this.minDate.setDate(this.minDate.getDate() - 1);
	}

	public get decimalFormat(): string {
		return this.t('decimal-format');
	}

	public get integerFormat(): string {
		return this.t('integer-format');
	}

	public get intFormat(): string {
		return this.t('int-format');
	}

	public get phoneNumberFormat(): string {
		return this.t('phoneNumberFormat');
	}

	public getFormControl(controlName: string): AbstractControl {
		return this.form.get(controlName);
	}

	public getSubFormControlState(formGroupName: string, formControlName: string): boolean {
		return (
			(this.form.get(formGroupName).get(formControlName).invalid &&
				(this.form.get(formGroupName).get(formControlName).dirty ||
					this.form.get(formGroupName).get(formControlName).touched)) ||
			(this.form.get(formGroupName).get(formControlName).invalid && this.invalidForm)
		);
	}

	public getFormControlState(formControlName: string): boolean {
		return (
			(this.form.get(formControlName).invalid &&
				(this.form.get(formControlName).dirty || this.form.get(formControlName).touched)) ||
			(this.form.get(formControlName).invalid && this.invalidForm)
		);
	}

	/* debug methods */
	public log(...args): void {
		console.log.apply(null, args);
	}

	public init() {
		this.destroy$.subscribe((c) => console.log('🤔🤔🤔🤔'));
		const tableData = JSON.parse(this.utilServ.localStorageService.getItem(StorageKeys.TABLES_COLUMNS)) || {};

		this.translationInit(() => {
			this.utilityModel = [
				// {
				//   label: `${this.t('form.btn.export')} CSV`,
				//   icon: 'fas fa-download',
				//   command: () => {
				//     this.dt.exportCSV();
				//   }
				// },
				{
					label: `${this.t('excel')} XLSX`,
					icon: 'fas fa-file-excel',
					command: () => this.xlsExport(this.data, this.selectedColumns),
				},
			];
		});

		if (tableData && tableData[this.id]) {
			if (this.columns.length === tableData[this.id].columns.length) {
				this.columns = tableData[this.id].columns;
				this.selectedColumns = tableData[this.id].selectedColumns;
				const selectedSearchField = tableData[this.id].selectedSearchField;
				if (selectedSearchField && selectedSearchField.length > 0) {
					this.selectedSearchField = tableData[this.id].selectedSearchField;
				}
			} else {
				this.selectedColumns = this.columns.filter((i) => i.field !== 'id').filter((i) => i.default);
				//  .filter((i, index) => index <= 2);
				this.selectedSearchField = this.searchField.map((i) => i.value);
			}
		} else {
			this.selectedColumns = this.columns.filter((i) => i.field !== 'id').filter((i) => i.default);
			this.selectedSearchField = this.searchField.map((i) => i.value);
		}
		if (tableData && tableData[this.id]) {
			this.pageSize = tableData[this.id].pageSize || this.pageSize;
		}

		this.calendarInit();
	}

	public calendarInit() {
		this.translationInit(() => {
			this.calendar = this.t('calendar');
			if (this.hideLoaderAfterInit) {
				// this.hideloader();
			}
		});
	}

	xlsExport(data: any[], columns: TableColumns<any>, fileName = ''): void {
		const result = this.prepareExcelData(data, columns);
		const config = {
			filename: fileName,
			sheet: {
				data: result,
			},
		};
		zipcelx(config);
	}

	public prepareExcelData(data: any, columns: TableColumns<any>) {
		const result = [];

		// get selected columns
		const headers = (columns as any[]).map((item) => ({
			value: item.header,
			type: 'string',
		}));

		result.push(headers);

		data.forEach((value) => {
			const item = [];
			for (const col of columns) {
				if (col.type === 'date') {
					item.push({
						value: String(this.utilServ.dateService.transform(value[col.field], this.onlyDateFormat)),
						type: 'string',
					});
				} else if (col.type === 'number') {
					item.push({
						value: String(this.utilServ.maskService.transform(value[col.field] ?? 0, this.decimalFormat)),
						type: 'string',
					});
				} else if (col.translate) {
					item.push({
						value: String(this.utilServ.col.translateColumnValue(value[col.field], col, value)),
						type: 'string',
					});
				} else {
					item.push({
						value: String(value[col.field]),
						type: 'string',
					});
				}
			}
			result.push(item);
		});
		return result;
	}

	xlsExportWithData(data: any, fileName = 'excel-export'): void {
		const config = {
			filename: fileName,
			sheet: {
				data: data,
			},
		};
		zipcelx(config);
	}

	public ngOnDestroy(): void {
		const selectedColumns = this.selectedColumns;
		const columns = this.columns;
		const tableData = JSON.parse(this.utilServ.localStorageService.getItem(StorageKeys.TABLES_COLUMNS)) || {};

		const selectedSearchField = this.selectedSearchField;

		tableData[this.id] = {
			selectedColumns,
			columns,
			selectedSearchField: selectedSearchField,
			pageSize: this.pageSize,
		};

		this.utilServ.localStorageService.setItem(StorageKeys.TABLES_COLUMNS, JSON.stringify(tableData));

		this.destroy$.next(true);
	}

	public create() {
		if (this.isInValid()) {
			return;
		}
		this.showLoader();
		const model = Object.assign({}, this.form.value, { valid: true });
		this.componentService.createObject(model).subscribe(this.createHandler());
	}

	public update() {
		if (this.isInValid()) {
			return;
		}

		this.showLoader();
		const model = Object.assign({}, this.selectedItem, this.form.value);
		this.componentService.updateObject(model.id, model).subscribe(this.updateHandler());
	}

	protected t(key: string): string {
		return this.utilServ._translateService.instant(key);
	}

	protected translationInit(translateCallback: () => void): void {
		this.utilServ._translateService.get('g').subscribe(() => {
			if (translateCallback) {
				translateCallback();
			}
		});
	}

	//TODO: rename setBreadcrumbItem
	public setBreadcrumdItem(items: Array<{ label: string; routerLink?: string; url?: string }> = []): void {
		this.translationInit(() => {
			items.forEach((i) => {
				i.label = this.t(i.label);
			});
			this.utilServ._breadcrumbServer.updateItems(items);
		});
	}

	/* */
	public reset(): void {
		this.showLoader();
		this.searchForm.reset();
		this.searchObject = {};
		this.getPageData();
	}

	public loadLazy(e: any): void {
		if (!this.initStarted) {
			return;
		}
		this.pageNumber = e.first / e.rows;
		this.sortField = e.sortField || this.sortFieldDef || 'creDate';
		this.sortOrder = e.sortOrder === 1 ? 'DESC' : 'ASC';
		this.pageSize = e.rows;
		if (this.componentService && this.componentService.apiUrl) {
			this.getPageData();
		}
	}

	public getPageData(): Promise<any> {
		// set null for all field that have ('') empty string
		this.searchObjectCleanUp();

		if (typeof this.searchObject === 'string') {
		}
		// debugger
		return new Promise((resolve) => {
			if (this.componentService) {
				this.componentService
					.findPageableObjects(
						this.pageNumber,
						this.pageSize,
						`${this.sortField || 'creDate'},${this.sortOrder || 'DESC'}`,
						this.searchObject
					)
					.pipe(takeUntil(this.destroy$))
					.subscribe((res: PaginationResult<any>) => {
						this.total = res.totalElements;
						if (this.isLocalFilter) {
							// this.searchForm.reset({}, { emitEvent: false });
							this.localFilterFormReset();
						}
						this.data = res.content;
						this.cashData = res.content;
						this.hideloader();
						resolve(null);
					});
			}
		});
	}

	public getFirstPage() {
		this.pageNumber = 0;
		return this.getPageData();
	}

	public toSelectedItem(res: any[]): any[] {
		return res.map((item) => {
			return { label: item.name, value: item.id };
		});
	}

	public initActionMenu(items: MenuItem[] = [], removeDefualt: boolean = false): void {
		if (removeDefualt) {
			this.actionMenuModel = items;
		} else {
			this.actionMenuModel = [
				{
					label: this.t('theme.action-button.update'),
					icon: 'fas fa-pencil-alt',
					command: (data) => {
						this.selectedItem = data;
						this.__showDialog(false);
					},
				},
				{
					label: this.t('theme.action-button.delete'),
					icon: 'fas fas fa-trash',
					command: (data) => {
						this.selectedItem = data;
						this.deleteDialog();
					},
					styleClass: 'ui-button-danger',
				},
				...items,
			];
		}
	}

	public initStepItems(items: string[] = []): void {
		const result = [];
		if (items.length > 0) {
			for (let i = 0, l = items.length; i < l; i++) {
				result.push({
					label: this.t(items[i]),
					command: () => (this.activeIndex = i),
				});
			} // for end

			this.stepItems = result;
		}
	}

	public deleteDialog(
		item?: any,
		callback?: () => void,
		config?: { message: string; alert?: { title: string; message: string } }
	) {
		config = config || { message: 'pages.message.main-message' };
		config.alert = config.alert || {
			title: 'confirmation.success-message.title.deleted',
			message: 'confirmation.success-message.message.deleted',
		};

		item = item || this.selectedItem;
		this.utilServ.confirmationService.confirm({
			header: this.t('pages.message.caution'),
			message: this.t(config.message),
			acceptLabel: 'form.btn.yes',
			accept: () => {
				this.componentService.deleteObject(item.id).subscribe((res) => {
					this.utilServ.alertService.success(this.t(config.alert.title), this.t(config.alert.message));
					this.getPageData();
					if (callback) {
						callback();
					}
				});
			},
		});
	}

	public __showDialog(states: boolean) {}

	public createHandler = (cb?: (args?) => void) => {
		return {
			next: (result) => {
				this.showCreateMessage();
				this.getFirstPage();
				this.utilServ.progressButtonServie.enable();
				if (cb) {
					cb(result);
				}
			},
			error: (err) => {
				this.utilServ.progressButtonServie.enable();
			},
			complete: () => {
				// this.enableProgressButtons();
				this.hideloader();
				this.hideDialog();
			},
		};
	};

	public showCreateMessage() {
		this.utilServ.alertService.success(this.t('alert.title.success'), this.t('alert.message.succesfully-created'));
	}

	public updateHandler = (cb?: (args?) => void) => {
		return {
			next: (result) => {
				this.showUpdateMessage();

				this.getFirstPage();

				if (cb) {
					cb(result);
				}
			},
			error: (err) => {},
			complete: () => {
				// this.enableProgressButtons();
				this.hideloader();
				this.hideDialog();
			},
		};
	};

	public showUpdateMessage() {
		this.utilServ.alertService.success(this.t('alert.title.success'), this.t('alert.message.succesfully-updated'));
	}

	public showApproveMessage() {
		this.utilServ.alertService.success(this.t('alert.title.success'), this.t('alert.message.succesfully-approved'));
	}

	public completeHandler = (cb: () => void = undefined, type: boolean = true) => {
		return {
			next: () => {},
			error: (err) => {},
			complete: () => {
				// this.enableProgressButtons();
				this.hideDialog();

				this.utilServ.alertService.success(
					this.t('alert.title.success'),
					this.t(type ? 'alert.message.succesfully-created' : 'alert.message.succesfully-updated')
				);

				this.getFirstPage();

				if (cb) {
					cb();
				}
			},
		};
	};

	public hideDialog() {
		this.isDialogVisible = false;
	}

	public hideloader(timeOut: number = 500): Promise<any> {
		return new Promise((res) => {
			setTimeout(() => {
				this.utilServ.loginloader.isLoading = false;
				res(null);
			}, timeOut);
		});
	}

	public showLoader(timeOut = 7500): void {
		this.utilServ.loginloader.isLoading = true;

		// setTimeout(() => {
		//   if (this.utilServ.loginloader.isLoading) {
		//     this.utilServ.loginloader.isLoading = false;
		//     console.log('🛑 hide from save fallback method after 7500 seconds.');
		//   }
		// }, timeOut);
	}

	// search form filter
	public isVisible(key: string): boolean {
		if (this.selectedSearchField && this.selectedSearchField.length > 0) {
			return this.selectedSearchField.indexOf(key) !== -1;
		} else {
			return false;
		}
	}

	public after(cb: any, timeout: number = 1): void {
		setTimeout(() => {
			if (cb instanceof Function) {
				cb();
			}
		}, timeout);
	}

	public search(): void {
		this.showLoader();
		this.pageNumber = 0;
		this.searchObject = this.searchForm.value;
		this.getPageData();
	}

	public resetUi() {
		this.activeIndex = 0;
		this.toDelete = null;
		this.file = null;
		this.selectedItem = null;
	}

	public stateListInit(): void {
		this.statesKeys.forEach((value) => {
			this.statusOptions.push({
				label: this.t(this.utilServ.col.getStateKey(value)),
				value,
			});
		});
	}

	public getCurrencySymbole(currency = ''): string {
		return currency === 'TRY' ? '₺' : '$';
	}

	public resetColumnFilter(): void {
		this.selectedColumns = this.columns
			// .filter(i => i.field !== 'id')
			.filter((i) => i.default);
	}

	public isInValid(): boolean {
		this.invalidForm = true;
		const invalid = this.form.invalid;

		if (invalid && this.formsCon) {
			const elm: HTMLDivElement = this.formsCon.nativeElement;

			const target: HTMLElement = elm.querySelector('.ng-invalid');
			if (target) {
				target.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center',
				});

				if (target.focus && typeof target.focus === 'function') {
					target.focus();
				}
			}
		}

		return invalid;
	}

	public startDialogAction(type: 'create' | 'update' = 'create', data?: any, isAsyncCall: boolean = false): void {
		this.invalidForm = false;
		if (this.form) {
			this.form.reset();
		}
		this.activeIndex = 0;
		this.dialogType = true;
		this.states.autoComplate = null;

		if (type === 'create') {
			this.dialogTitle = this.t('create');
			this.initCreateDialog();
			this.selectedItem = null;
		} else {
			this.selectedItem = data;
			this.dialogTitle = this.t('update');
			this.dialogType = false;
			this.initUpdateDialog(data);
		}

		if (!isAsyncCall) {
			this.after(() => (this.isDialogVisible = true));
		}
	}

	public initCreateDialog(): void {}

	public initUpdateDialog(data: any, e?: any): void {
		this.form.reset({ ...data });
	}

	public showCreateDialog(isAsyncCall: boolean = false) {
		this.startDialogAction('create', null, isAsyncCall);
	}

	public showUpdateDialog(data: any, isAsyncCall: boolean = false): void {
		this.startDialogAction('update', data, isAsyncCall);
	}

	public setLocalStorage(key: string, value: any): void {
		this.utilServ.localStorageService.setItem(key, JSON.stringify(value));
	}

	public getLocalStorage(key: string): any {
		return JSON.parse(this.utilServ.localStorageService.getItem(key));
	}

	public getValue(value: any, key: TranslationKey) {
		return this.utilServ.col.getValue(value, key);
	}

	public nextDayCalculator(dateTime: Date): string {
		const date = `${dateTime.getFullYear()}-${dateTime.getMonth() + 1}-${dateTime.getDate() + 1}`;
		return this.utilServ.dateService.transform(date, 'yyyy-MM-ddTHH:mm:ss');
	}

	get createState() {
		// tslint:disable-next-line: triple-equals
		return this.dialogType == true;
	}

	get updateState() {
		// tslint:disable-next-line: triple-equals
		return this.dialogType == false;
	}

	/**
	 * run t method on the label value so we get a translated list 🍉.
	 */
	public getTranslatedList(list: LabelValue<string, any>[]): LabelValue<string, any>[] {
		return list.map(({ label, value }) => ({ label: this.t(label), value }));
	}

	protected initSearchFields(): void {
		this.searchField = this.columns
			.filter((col) => col.searchField)
			.map((col) => ({ label: col.header, value: col.field as string }));
	}

	confirm({ title = 'warning', message = 'confirmOperation', passConfirm = false } = {}): Promise<any> {
		return new Promise((res) => {
			if (passConfirm) {
				res(null);
			} else {
				this.utilServ.confirmationService.confirm({
					header: this.t(title),
					message: this.t(message),
					accept: () => res(null),
					acceptLabel: 'form.btn.yes',
				});
			}
		});
	}

	setPdfHeader() {}

	async pdfExport(or: 'landscape' | 'portrait' = 'portrait') {
		this.showLoader();
		this.pdfHeader = [];
		this.setPdfHeader();
		await pdf(this.dt.el.nativeElement, or, this.removeLastColumn, this.pdfHeader, this.selectedColumns);
		this.hideloader();
	}

	searchObjectCleanUp() {
		for (const key of Object.keys(this.searchObject)) {
			if (this.searchObject[key] === null || this.searchObject[key] === '') {
				delete this.searchObject[key];
			}
		}
	}

	filter(searchArgs = {}) {
		const searchKeys = Object.entries(searchArgs).filter(([key, value]) => value);

		this.data = this.cashData.filter((res) => {
			return this.filterObj(searchKeys, res);
		});
	}

	convertToBaseDate(value: Date | number): string {
		return this.utilServ.dateService.transform(value instanceof Date ? value : new Date(value), ' dd/MM/yy');
	}

	filterObj(searchKeys = [], obj = {}) {
		if (searchKeys.length) {
			let result = true;
			searchKeys.forEach(([key, value]) => {
				if (this.searchDateFields.includes(key)) {
					result = result && this.convertToBaseDate(obj[key]) === this.convertToBaseDate(value);
				} else if (obj[key] instanceof Array) {
					console.log('🙋‍♂️💣');

					const searchArgsValues = (obj[key] as any[]).map((v) => String(v).toLocaleLowerCase('tr'));
					result = result && searchArgsValues.includes(String(value ?? '').toLocaleLowerCase('tr'));
				} else {
					result =
						result &&
						String(obj[key])
							.toLocaleLowerCase('tr')
							.includes((value || '').toLocaleLowerCase('tr'));
				}
			});
			return result;
		} else {
			return true;
		}
	}

	initDataTableFilter(fg?: FormGroup): void {
		this.isLocalFilter = true;
		const searchFrom: FormGroup = fg || this.searchForm;
		this.localFilterForm = searchFrom;
		searchFrom.valueChanges.pipe(debounceTime(500)).subscribe((searchValue) => {
			this.filter(searchValue);
		});
	}

	localFilterFormReset() {
		this.localFilterForm.reset({}, { emitEvent: false });
	}

	//TODO: move report-util
	updateFormWithConditionalFormatting(e) {
		this.form.value.forEach((elem, index) => {
			if (index === e.index) {
				if (
					(this.form.controls[index] as FormGroup).get('conditionalFormattingConfiguration') === undefined ||
					(this.form.controls[index] as FormGroup).get('conditionalFormattingConfiguration') === null
				) {
					(this.form.controls[index] as FormGroup).addControl('conditionalFormattingConfiguration', new FormControl(e));
				} else {
					(this.form.controls[index] as FormGroup).get('conditionalFormattingConfiguration').setValue(e);
				}
			}
		});
	}

	//TODO: move report-util
	conditionalFormattingHandler(field, index): void {
		this.conditionalFormattingField = field;
		this.conditionalFormattingIndex = index;
		this.conditionalFormattingObject = JSON.parse(JSON.stringify(this.form.controls[index].value));
		const condition = this.form.controls[index].value.conditionalFormattingConfiguration;
		this.conditionalFormattingConditions = condition ? [...condition] : [];
	}

	//TODO: move report-util
	// coloring fields starts
	selectedColoredField: Key;
	setColoredField(field: Key): void {
		this.selectedColoredField = field;
	}

	//TODO: Gereksizler silinecek
	initColoredFields(columns?) {
		if (columns) {
			columns.forEach((field) => {
				const colorSettings = field.colorSettings;
				if (colorSettings) {
					this.encoloredFields.push(field.colorSettings);
				}
			});
		} else {
			const allFields = [...this.columnsList, ...this.rowsList];
			allFields.forEach((field) => {
				const colorSettings = field.colorSettings;
				if (colorSettings) {
					this.encoloredFields.push(field.colorSettings);
				}
			});
		}
	}

	//TODO: move report-util
	saveColoredFields(columns?) {
		if (columns) {
			this.encoloredFields.forEach((field: EncoloredField) => {
				const singleField = columns.find((report) => report.name === field.key);
				if (singleField) {
					singleField.colorSettings = field;
				}
			});
		} else {
			const allFields = [...this.columnsList, ...this.rowsList];

			this.encoloredFields.forEach((field: EncoloredField) => {
				const singleField = allFields.find((report) => report.name === field.key);
				if (singleField) {
					singleField.colorSettings = field;
				}
			});
		}
	}

	//TODO: move report-util

	pushToEncoloredFields({ selectedColor, colorFor }: { selectedColor: string; colorFor: string }) {
		if (!this.encoloredFields) {
			this.encoloredFields = [];
		}
		const isFieldExist = this.encoloredFields.find((field: EncoloredField) => field.key === this.colorFieldKey);
		if (isFieldExist) {
			if (colorFor === 'header') {
				isFieldExist.colorHeader = selectedColor;
			} else {
				isFieldExist.colorCell = selectedColor;
			}
			isFieldExist.colorFor[colorFor] = true;
		} else {
			const colorForObject = { header: false, cell: false };
			colorForObject[colorFor] = true;
			if (colorFor === 'header') {
				const model = {
					colorHeader: selectedColor,
					colorFor: colorForObject,
					key: this.colorFieldKey,
					colorCell: '',
				};
				this.encoloredFields.push(model);
			} else {
				const model = {
					colorHeader: '',
					colorFor: colorForObject,
					key: this.colorFieldKey,
					colorCell: selectedColor,
				};
				this.encoloredFields.push(model);
			}
		}
	}

	// coloring fields ends
} // the end
